import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import {
  InputGroup,
  Dropdown,
  DropdownButton,
  Form,
} from 'react-bootstrap';
import SearchTag from './SearchTag';
import {
  SearchParams,
  FreeTextTag,
  DynamicTag,
  AdvancedTag,
  AllowedTypeFilters,
  getTypeLabel,
} from '../../utils/searchUtils';
import { retrieveSimpleSearch } from '../../actions/umoGallery';
import { setMetaUmoId } from '../../actions/mainUI';
import SelectorIcon from '../../../icons/sub-menu-and-common-icons/selector.svg';
import SearchIcon from '../../../icons/sub-menu-and-common-icons/search-normal.svg';

function SearchSimple({
  searchParams,
  dispatch,
  intl,
}) {
  const [searchValue, setSearchValue] = useState('');
  const [runSearch, setRunSearch] = useState(false);
  const [tagList, setTagList] = useState([]);
  const [advancedTags, setAdvancedTags] = useState([]);
  const [typeFilter, setTypeFilter] = useState(searchParams.get('typeFilter') || '');

  const typeMenuItems = ['', ...AllowedTypeFilters].map((type) => (
    <Dropdown.Item eventKey={type} key={type || ''}>
      {intl.formatMessage({ id: getTypeLabel(type) })}
    </Dropdown.Item>
  ));

  const newTag = (value) => {
    if (value.includes(':')) {
      return new DynamicTag(value, value);
    }
    return new FreeTextTag(value, value);
  };

  const onSubmit = (e) => {
    if (e) e.preventDefault();
    dispatch(setMetaUmoId(null));
    const newTagList = searchParams.get('tagList') ? [...tagList] : [];
    if (searchValue !== '') {
      const tag = newTag(searchValue);
      newTagList.push(tag);
    }
    setTagList(newTagList);
    setRunSearch(true);
  };

  useEffect(() => {
    if (runSearch && (tagList.length > 0 || advancedTags.length > 0)) {
      dispatch(retrieveSimpleSearch(null, typeFilter, tagList, advancedTags));
      setRunSearch(false);
      setSearchValue('');
    }
  }, [tagList, typeFilter, searchParams, dispatch, runSearch]);

  useEffect(() => {
    setTypeFilter(searchParams.get('typeFilter'));
    setAdvancedTags(searchParams.get('metadataList') || []);
    setTagList(searchParams.get('tagList') || []);
  }, [searchParams]);

  const removeTag = (searchTag) => {
    if (tagList.length > 0) {
      const newTags = tagList.filter(tag => tag !== searchTag);
      setTagList(newTags);
      setRunSearch(true);
    }
  };

  const removeAdvancedTag = () => {
    if (advancedTags.length > 0) {
      setAdvancedTags([]);
      setRunSearch(true);
    }
  };

  const onSearchInputChange = (e) => {
    setSearchValue(e.target.value);
  };

  const onSearchInputKeyDown = (e) => {
    if (e.keyCode === 8 && searchValue === '') {
      // backspace key, not while typing new word
      if (tagList.length > 0) {
        removeTag(tagList.length > 0 ? tagList[tagList.length - 1] : advancedTags[0]);
      } else if (advancedTags.length > 0) {
        removeAdvancedTag();
      }
    }
  };

  const editTag = (searchTag, newValue) => {
    if (tagList.length > 0 && newValue) {
      const newTags = tagList.map(tag => ((tag === searchTag) ? newTag(newValue) : tag));
      setTagList(newTags);
      setRunSearch(true);
    }
  };

  const displayTags = [];
  if (advancedTags?.length > 0) {
    displayTags.push(
      <SearchTag
        key="advanced-search-tag"
        searchTag={new AdvancedTag(
          null,
          intl.formatMessage({ id: 'searchBar.advancedSearch' }),
          intl.formatMessage({ id: 'searchBar.advancedSearch' })
        )}
        onRemoveClick={removeAdvancedTag}
      />
    );
  }
  tagList?.forEach((searchTag, i) => {
    displayTags.push(
      <SearchTag
        key={i}
        searchTag={searchTag}
        onRemoveClick={removeTag}
        onEditClick={editTag}
      />
    );
  });

  return (
    <div className="search-simple">
      <form onSubmit={onSubmit} className="form search-simple__form">
        <InputGroup className="search-simple__ip">
          <div className="search-simple__tags">{displayTags}</div>
          <Form.Control
            className="search-simple__input font-sm-regular"
            placeholder={intl.formatMessage({
              id: 'searchBar.simple.searchPlaceholder',
            })}
            type="text"
            onKeyDown={onSearchInputKeyDown}
            value={searchValue}
            onChange={onSearchInputChange}
          />
        </InputGroup>
        <DropdownButton
          onSelect={eventKey => setTypeFilter(eventKey)}
          title={(
            <div className="search-simple__type-input-header">
              <div>
                {intl.formatMessage({ id: getTypeLabel(typeFilter) })}
              </div>
              <img src={SelectorIcon} />
            </div>
          )}
          size="sm"
          variant="default"
          className="search-simple__type-input-button font-xs-regular"
          id="simple-input-dropdown-addon"
        >
          {typeMenuItems}
        </DropdownButton>
        <button
          variant="primary"
          onClick={onSubmit}
          className="search-simple__search-button"
        >
          <img src={SearchIcon} className="search-simple__search-icon" />
        </button>
      </form>
    </div>
  );
}

SearchSimple.propTypes = {
  searchParams: PropTypes.instanceOf(SearchParams),
  dispatch: PropTypes.func,
};

export default injectIntl(SearchSimple);
